"use client";

import { useEffect, useRef } from "react";

import type { PublicCatalogProduct } from "@/domain/catalog/catalog-product";
import { homeContent } from "@/data/home-content";
import { ProductCard } from "@/components/home/product-card";
import { SectionHeader } from "@/components/home/section-header";
import { MotionList, MotionListItem, MotionSection } from "@/components/ui/motion-primitives";

export interface ProductSectionProps {
  readonly actionHref?: string;
  readonly familySlug: string;
  readonly title: string;
  readonly products: readonly PublicCatalogProduct[];
  readonly hasMore?: boolean;
  readonly isLoading?: boolean;
  readonly errorMessage?: string | null;
  readonly onLoadMore?: () => void;
  readonly onRetry?: () => void;
}

export function ProductSection({
  actionHref,
  familySlug,
  products,
  title,
  hasMore = false,
  isLoading = false,
  errorMessage = null,
  onLoadMore,
  onRetry,
}: Readonly<ProductSectionProps>) {
  const listRef = useRef<HTMLDivElement>(null);
  const verticalSentinelRef = useRef<HTMLDivElement>(null);
  const horizontalSentinelRef = useRef<HTMLDivElement>(null);
  const fallbackButtonRef = useRef<HTMLButtonElement>(null);
  const loadMoreRef = useRef(onLoadMore);

  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  useEffect(() => {
    const fallback = fallbackButtonRef.current;
    if (fallback) {
      fallback.hidden = typeof IntersectionObserver !== "undefined";
    }
  });

  useEffect(() => {
    if (!hasMore || !onLoadMore || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    const isMobile = window.matchMedia?.("(max-width: 767px)").matches ?? false;
    const target = isMobile
      ? horizontalSentinelRef.current
      : verticalSentinelRef.current;
    const root = isMobile ? listRef.current : null;

    if (!target) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting && !isLoading) {
          loadMoreRef.current?.();
        }
      },
      {
        root,
        rootMargin: isMobile ? "0px 720px 0px 0px" : "720px 0px",
      },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, isLoading, onLoadMore]);

  return (
    <MotionSection className="mt-4 [content-visibility:auto] [contain-intrinsic-size:0_360px]">
      <SectionHeader
        actionLabel={homeContent.sectionAction}
        href={actionHref ?? `/categorias/${familySlug}`}
        title={title}
      />

      <div
        className="hide-scrollbar -mx-4 overflow-x-auto px-4 pb-2 md:mx-0 md:overflow-visible md:px-0 md:pb-0"
        ref={listRef}
      >
        <MotionList className="flex gap-4 md:grid md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 md:gap-6">
          {products.map((product) => (
            <MotionListItem key={product.id}>
              <ProductCard product={product} />
            </MotionListItem>
          ))}
          {onLoadMore ? (
            <span
              aria-hidden="true"
              className="h-px w-px shrink-0 md:hidden"
              ref={horizontalSentinelRef}
            />
          ) : null}
        </MotionList>
      </div>
      {onLoadMore ? (
        <div aria-hidden="true" className="hidden h-px md:block" ref={verticalSentinelRef} />
      ) : null}
      {onLoadMore && (hasMore || isLoading || errorMessage) ? (
        <div aria-live="polite" className="mt-3 min-h-8 text-center text-sm font-bold text-muted">
          {errorMessage ? (
            <div className="flex flex-col items-center gap-2">
              <p>{errorMessage}</p>
              <button
                className="rounded-md bg-primary px-4 py-2 text-sm font-bold text-white"
                onClick={onRetry ?? onLoadMore}
                type="button"
              >
                Reintentar
              </button>
            </div>
          ) : isLoading ? (
            "Cargando productos..."
          ) : hasMore ? (
            <button
              className="rounded-md border border-primary px-4 py-2 text-sm font-bold text-primary"
              hidden
              onClick={onLoadMore}
              ref={fallbackButtonRef}
              type="button"
            >
              Cargar más
            </button>
          ) : null}
        </div>
      ) : null}
    </MotionSection>
  );
}
